import { useEffect, useRef, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { useLazyQuery, useMutation } from "@apollo/client";
import { useToast, useRadioGroup } from "@chakra-ui/react";
import { format } from "date-fns";
import { QAPP, GETQAPPLEVELSTATUS } from "../../graphql/queries";
import { GENERATEQSET } from "../../graphql/mutations";
import {
  getAvgTimeTakenPerQuestion,
  secondsToMinutesAndSeconds,
} from "../../utils/helper";

export const useHome = ({ user, loadingCallback }) => {
  const [myApp, setMyApp] = useState({});
  const [qsets, setQsets] = useState([]);
  const [myBadges, setMyBadges] = useState([]);
  const [levels, setLevels] = useState([]);
  const [selectedLevel, setSelectedLevel] = useState("");
  const [currentQSet, setCurrentQSet] = useState({});
  const [qAppLevelStatus, setQAppLevelStatus] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const { search } = useLocation();
  const history = useHistory();
  const queryParams = new URLSearchParams(search);
  const appid = queryParams.get("appid");
  const userid = queryParams.get("userid");
  const previewModeParam = (queryParams.get("preview_mode") || "").toLowerCase();
  const isPreviewMode = ["1", "true", "yes"].includes(previewModeParam);
  const directModeParam = (queryParams.get("direct_mode") || queryParams.get("direct") || "").toLowerCase();
  const isDirectMode = ["1", "true", "yes"].includes(directModeParam);
  const effectiveUserId = (isPreviewMode || isDirectMode) ? (userid || user.id) : user.id;

  const toast = useToast();
  // Remembers which button kicked off generateQSet so onCompleted opens the set in the right mode.
  const isLearnLaunchRef = useRef(false);

  const qviewerUrl = (qsetId, isLearnMode) =>
    `/qviewer/?appid=${appid}&userid=${userid}&qsetid=${qsetId}${isPreviewMode ? "&preview_mode=1" : ""}${isDirectMode ? "&direct_mode=1" : ""}${isLearnMode ? "&mode=learn" : ""}`;

  const [getMyApp] = useLazyQuery(QAPP, {
    onCompleted: (data) => {
      const products = data?.qapp?.app?.products || [];
      const preReqs = data?.qapp?.app?.bundleAppProp?.preReqs || [];
      const mappedLevels = products.map((product, index) => ({
        id: product.id,
        name: product.productName || `Level ${index + 1}`,
        criteria: preReqs[index],
      }));
      const defaultLevel = mappedLevels[0]?.name || "";

      setMyApp(data?.qapp);
      setCurrentQSet(data?.qsets.reduce((pv, cv) => {
        if (cv.qSetSubmissions.some(submission => !submission.isAllQuestionSubmitted) || cv.qSetSubmissions.length === 0) {
            pv = cv;
        }
        return pv;
      }, {}));
      setQsets(
        data?.qapp?.qsets.map((qset) => {
          const submission =
            qset?.qSetSubmissions.length > 0 ? qset?.qSetSubmissions[0] : {};
          const totalQuestions = submission.id
            ? submission?.qset?.dynosUnderQSet.length
            : null;
          return {
            ...qset,
            name: qset?.qSetName,
            submittedAt: submission?.id && submission?.isAllQuestionSubmitted
              ? format(new Date(submission?.updatedAt), "MM/dd/yyyy")
              : null,
            score: submission?.id && submission?.isAllQuestionSubmitted
              ? `${submission?.qsetScore}/${totalQuestions}`
              : null,
            totalTimeTaken: submission?.id && submission?.isAllQuestionSubmitted
              ? secondsToMinutesAndSeconds(submission?.qsetTimeTaken)
              : null,
            avgTimeTaken: submission?.id && submission?.isAllQuestionSubmitted
              ? getAvgTimeTakenPerQuestion(
                Number(submission?.qsetTimeTaken),
                Number(totalQuestions),
              )
              : null,
            status: submission?.id
              ? submission?.isAllQuestionSubmitted
                ? "View Responses"
                : "Continue"
              : "Try Now",
          };
        }),
      );
      const userBadges = data?.qapp?.myBadges;
      setMyBadges(
        data?.qapp?.app?.bundleAppProp?.badges?.reduce(
          (previousValue, currentValue) => {
            const index = userBadges.findIndex(
              (ub) => ub.id === currentValue.id,
            );
            previousValue.push({
              ...currentValue,
              isLocked: index === -1,
            });
            return previousValue;
          },
          [],
        ),
      );
      setLevels(mappedLevels);
      setSelectedLevel(defaultLevel);
      setValue(defaultLevel);
      sessionStorage.setItem("appName", data?.qapp?.app?.bundleName);
      loadingCallback(false, "");
      setIsLoading(false);
    },
    onError: () => {
      loadingCallback(false, "");
    },
    fetchPolicy: "network-only"
  });

  const [getQAppLevelStatus] = useLazyQuery(GETQAPPLEVELSTATUS, {
    onCompleted: (data) => {
      setQAppLevelStatus(data.getQappLevelStatus)
    },
    onError: () => {
      loadingCallback(false, "");
    },
    fetchPolicy: "network-only"
  })

  const [generateQSet] = useMutation(GENERATEQSET, {
    onCompleted: (data) => {
      loadingCallback(false, "");
      setMyApp({
        ...myApp,
        qsets: [...myApp.qsets, data.generateQSet],
      });
      setCurrentQSet(data.generateQSet);
      toast({
        position: "top",
        title: "Generated question set.",
        description: "We've generated question set for you.",
        status: "success",
        duration: 4000,
      });
      history.push(qviewerUrl(data.generateQSet.id, isLearnLaunchRef.current));
    },
    onError: () => {
      loadingCallback(false, "");
      toast({
        position: "top",
        title: "Something went wrong please try again after sometime.",
        status: "error",
        duration: 4000,
      });
    },
  });

  useEffect(() => {
    if (effectiveUserId && appid) {
      loadingCallback(true, "Loading app...");
      getQAppLevelStatus({variables: {
        where: {
          id: appid,
          userId: effectiveUserId
        }
      }})
      getMyApp({
        variables: {
          where: {
            id: appid,
          },
          qsetwhere: {
            AND: [
              {
                QApp: {
                  id: {
                    equals: appid,
                  },
                },
              },
              {
                userId: {
                  equals: effectiveUserId,
                },
              },
            ],
          },
        },
      });
    }
  }, [appid, effectiveUserId, getMyApp, getQAppLevelStatus, loadingCallback]);

  // Learn mode uses the same question sets as a test; it only changes how /qviewer reveals answers.
  const continuePreviousTestOrcreateQset = (isLearnMode = false) => {
    const levelToUse = selectedLevel || levels[0]?.name || "";

    if (currentQSet?.id) {
      history.push(qviewerUrl(currentQSet?.id, isLearnMode));
      return true;
    }
    if (!levelToUse) {
      toast({
        position: "top",
        title: "No level is configured for this app.",
        status: "info",
        duration: 4000,
      });
      return true;
    }
    isLearnLaunchRef.current = isLearnMode;
    loadingCallback(true, "generating question sets");
    generateQSet({
      variables: {
        data: {
          id: appid,
          userId: effectiveUserId,
          dynoType: "Practice",
          level: levelToUse,
        },
      },
    });
  };

  const { getRootProps, getRadioProps, setValue } = useRadioGroup({
    name: "level",
    onChange: (value) => setSelectedLevel(value),
  });

  const group = getRootProps();

  return {
    myApp,
    qsets,
    currentQSet,
    myBadges,
    levels,
    selectedLevel,
    appid,
    userid,
    isPreviewMode,
    isDirectMode,
    effectiveUserId,
    history,
    group,
    qAppLevelStatus,
    isLoading,
    getRadioProps,
    continuePreviousTestOrcreateQset,
    startLearning: () => continuePreviousTestOrcreateQset(true),
  };
};
